import React, { useReducer, useCallback, useEffect } from "react";
import GithubContext from "./githubContext";
import GithubReducer from "./githubReducer";
import github, { parseRateLimitHeaders } from "./githubApi";
import { FEATURED_DEVELOPER_LOGINS } from "../../data/featuredDevelopersList";
import {
  buildUserSearchQuery,
  buildRepoSearchQuery,
} from "../../utils/githubSearchQueries";
import {
  apiErrorBanner,
  buildApiErrorFromAxios,
} from "../../utils/githubApiErrors";
import {
  SEARCH_USERS,
  SET_SEARCH_META,
  SET_LOADING,
  SET_SEARCH_LOADING,
  GET_REPOS,
  CLEAR_USERS,
  CLEAR_USER_SEARCH_RESULTS,
  GET_USER,
  SET_RATE_LIMIT,
  SET_API_ERROR,
  CLEAR_API_ERROR,
  CACHE_PROFILE,
  SET_FEATURED_LOADING,
  SET_FEATURED_USERS,
  SET_REPOS_FOR_LOGIN,
  APPEND_REPOS_FOR_LOGIN,
  SET_CARD_REPOS_LOADING,
  SET_INSIGHT_LOGIN,
  SET_INSIGHT_BUNDLE,
  SEARCH_REPOS,
  CLEAR_REPO_SEARCH_RESULTS,
  SET_REPO_SEARCH_META,
  SET_REPO_SEARCH_LOADING,
} from "../types";

export { buildUserSearchQuery, buildRepoSearchQuery } from "../../utils/githubSearchQueries";

const PER_PAGE = 12;
const REPO_PAGE = 12;

const GithubState = (props) => {
  const initialState = {
    users: [],
    repos: [],
    user: {},
    repoResults: [],
    loading: false,
    searchLoading: false,
    repoSearchLoading: false,
    featuredLoading: true,
    featuredUsers: [],
    profilesByLogin: {},
    reposByLogin: {},
    languageStatsByLogin: {},
    insightByLogin: {},
    searchMeta: { total: 0, page: 1, perPage: PER_PAGE },
    repoSearchMeta: { total: 0, page: 1, perPage: PER_PAGE },
    insightLogin: null,
    rateLimit: { remaining: null, resetAt: null },
    apiError: null,
  };

  const [state, dispatch] = useReducer(GithubReducer, initialState);

  const applyRateLimit = (res) => {
    const rl = parseRateLimitHeaders(res);
    dispatch({ type: SET_RATE_LIMIT, payload: rl });
  };

  const handleAxiosError = (err) => {
    dispatch({
      type: SET_API_ERROR,
      payload: buildApiErrorFromAxios(err),
    });
  };

  const searchUsers = async (text, filters = {}, page = 1) => {
    dispatch({ type: CLEAR_API_ERROR });
    const q = buildUserSearchQuery(text, filters);
    if (!q || q === "type:user") {
      dispatch({
        type: SET_API_ERROR,
        payload: apiErrorBanner(
          "Enter a search term or at least one filter."
        ),
      });
      return;
    }
    dispatch({ type: SET_SEARCH_LOADING, payload: true });
    if (page === 1) {
      dispatch({ type: CLEAR_USER_SEARCH_RESULTS });
      dispatch({ type: CLEAR_REPO_SEARCH_RESULTS });
      dispatch({
        type: SET_SEARCH_META,
        payload: { page: 1, total: 0, perPage: PER_PAGE },
      });
    }
    try {
      const sortMap = {
        followers: "followers",
        joined: "joined",
        repositories: "repositories",
      };
      const sort = sortMap[filters.sortUsers] || undefined;
      const res = await github.get("/search/users", {
        params: {
          q,
          page,
          per_page: PER_PAGE,
          ...(sort ? { sort, order: "desc" } : {}),
        },
      });
      applyRateLimit(res);
      const items = res.data.items || [];
      const total = res.data.total_count || 0;
      dispatch({
        type: SET_SEARCH_META,
        payload: { total, page, perPage: PER_PAGE },
      });
      dispatch({ type: SEARCH_USERS, payload: items });
      if (items.length && page === 1) {
        const first = items[0].login;
        dispatch({ type: SET_INSIGHT_LOGIN, payload: first });
        fetchInsightBundle(first);
      }
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const searchRepositories = async (text, filters = {}, page = 1) => {
    dispatch({ type: CLEAR_API_ERROR });
    const q = buildRepoSearchQuery(text, filters);
    if (!q) {
      dispatch({
        type: SET_API_ERROR,
        payload: apiErrorBanner("Enter a search query."),
      });
      return;
    }
    dispatch({ type: SET_REPO_SEARCH_LOADING, payload: true });
    if (page === 1) {
      dispatch({ type: CLEAR_REPO_SEARCH_RESULTS });
      dispatch({ type: CLEAR_USER_SEARCH_RESULTS });
      dispatch({
        type: SET_REPO_SEARCH_META,
        payload: { page: 1, total: 0, perPage: PER_PAGE },
      });
    }
    try {
      const sortRepo = filters.sortRepo || "stars";
      const sort = sortRepo === "updated" ? "updated" : "stars";
      const res = await github.get("/search/repositories", {
        params: {
          q,
          page,
          per_page: PER_PAGE,
          sort,
          order: "desc",
        },
      });
      applyRateLimit(res);
      const items = res.data.items || [];
      const total = res.data.total_count || 0;
      dispatch({
        type: SET_REPO_SEARCH_META,
        payload: { total, page, perPage: PER_PAGE },
      });
      dispatch({ type: SEARCH_REPOS, payload: items });
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const fetchFeaturedUsers = useCallback(async () => {
    const logins = FEATURED_DEVELOPER_LOGINS.map((l) => String(l).trim()).filter(Boolean);
    if (!logins.length) {
      dispatch({ type: SET_FEATURED_USERS, payload: [] });
      return;
    }
    dispatch({ type: SET_FEATURED_LOADING, payload: true });
    try {
      /** Don't reject on 404/403 so axios/browser don't log one bad login as a hard error. */
      const responses = await Promise.all(
        logins.map((login) =>
          github.get(`/users/${encodeURIComponent(login)}`, {
            validateStatus: (status) => status >= 200 && status < 500,
          })
        )
      );
      const users = [];
      const fulfilledResponses = [];
      responses.forEach((res) => {
        if (res.status !== 200 || !res.data) return;
        fulfilledResponses.push(res);
        const d = res.data;
        users.push({
          id: d.id,
          login: d.login,
          avatar_url: d.avatar_url,
          html_url: d.html_url,
        });
      });
      if (fulfilledResponses.length) {
        applyRateLimit(fulfilledResponses[fulfilledResponses.length - 1]);
      }
      dispatch({ type: SET_FEATURED_USERS, payload: users });
    } catch {
      dispatch({ type: SET_FEATURED_USERS, payload: [] });
    }
  }, [dispatch]);

  useEffect(() => {
    fetchFeaturedUsers();
  }, [fetchFeaturedUsers]);

  const fetchProfileForCard = async (login) => {
    if (!login || state.profilesByLogin[login]) return;
    try {
      const res = await github.get(`/users/${encodeURIComponent(login)}`);
      applyRateLimit(res);
      dispatch({
        type: CACHE_PROFILE,
        payload: { login, profile: res.data },
      });
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        return;
      }
      handleAxiosError(err);
    }
  };

  const fetchReposForCard = async (login, sort = "updated", page = 1, append = false) => {
    if (!login) return;
    dispatch({
      type: SET_CARD_REPOS_LOADING,
      payload: { login, loading: true },
    });
    try {
      const res = await github.get(`/users/${encodeURIComponent(login)}/repos`, {
        params: {
          per_page: REPO_PAGE,
          page,
          sort: sort === "stars" ? "stars" : "updated",
          direction: "desc",
          type: "owner",
        },
      });
      applyRateLimit(res);
      const items = res.data || [];
      const hasMore = items.length === REPO_PAGE;
      if (append) {
        dispatch({
          type: APPEND_REPOS_FOR_LOGIN,
          payload: { login, repos: items, page, hasMore },
        });
      } else {
        dispatch({
          type: SET_REPOS_FOR_LOGIN,
          payload: { login, repos: items, page, hasMore, sort },
        });
      }
    } catch (err) {
      const status = err.response?.status;
      if (status !== 404) {
        handleAxiosError(err);
      }
      dispatch({
        type: SET_CARD_REPOS_LOADING,
        payload: { login, loading: false },
      });
    }
  };

  const fetchInsightBundle = async (login) => {
    if (!login) return;
    try {
      const res = await github.get(`/users/${encodeURIComponent(login)}/repos`, {
        params: {
          per_page: 100,
          sort: "updated",
          direction: "desc",
          type: "owner",
        },
      });
      applyRateLimit(res);
      const repos = res.data || [];
      const languages = {};
      let totalStars = 0;
      let topRepo = null;
      repos.forEach((r) => {
        totalStars += r.stargazers_count || 0;
        const lang = r.language;
        if (lang) {
          languages[lang] = (languages[lang] || 0) + 1;
        }
        if (
          !topRepo ||
          (r.stargazers_count || 0) > (topRepo.stargazers_count || 0)
        ) {
          topRepo = r;
        }
      });
      dispatch({
        type: SET_INSIGHT_BUNDLE,
        payload: {
          login,
          data: {
            languages,
            totalStars,
            topRepo: topRepo
              ? {
                  name: topRepo.name,
                  html_url: topRepo.html_url,
                  stargazers_count: topRepo.stargazers_count,
                  description: topRepo.description,
                }
              : null,
            repoCount: repos.length,
            contributionHint:
              "Based on public repositories and stars (approximate).",
          },
        },
      });
    } catch (err) {
      const status = err.response?.status;
      if (status !== 404) {
        handleAxiosError(err);
      }
    }
  };

  const getUser = async (username) => {
    dispatch({ type: SET_LOADING });
    dispatch({ type: CLEAR_API_ERROR });
    try {
      const res = await github.get(`/users/${encodeURIComponent(username)}`);
      applyRateLimit(res);
      dispatch({ type: GET_USER, payload: res.data });
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const getUserRepos = async (username) => {
    dispatch({ type: SET_LOADING });
    try {
      const res = await github.get(`/users/${encodeURIComponent(username)}/repos`, {
        params: {
          per_page: 12,
          sort: "updated",
          direction: "desc",
          type: "owner",
        },
      });
      applyRateLimit(res);
      dispatch({ type: GET_REPOS, payload: res.data });
    } catch (err) {
      handleAxiosError(err);
    }
  };

  const clearUsers = () => dispatch({ type: CLEAR_USERS });

  const clearApiError = () => dispatch({ type: CLEAR_API_ERROR });

  /** Clears user/repo search results and insights when the search box is emptied (back to default home). */
  const resetHomeSearch = useCallback(() => {
    dispatch({ type: CLEAR_API_ERROR });
    dispatch({ type: CLEAR_USERS });
    dispatch({ type: CLEAR_REPO_SEARCH_RESULTS });
    dispatch({
      type: SET_REPO_SEARCH_META,
      payload: { total: 0, page: 1, perPage: PER_PAGE },
    });
    dispatch({ type: SET_REPO_SEARCH_LOADING, payload: false });
    dispatch({ type: SET_INSIGHT_LOGIN, payload: null });
  }, [dispatch]);

  const setInsightLogin = (login) => {
    dispatch({ type: SET_INSIGHT_LOGIN, payload: login });
    if (login) fetchInsightBundle(login);
  };

  return (
    <GithubContext.Provider
      value={{
        users: state.users,
        user: state.user,
        repos: state.repos,
        repoResults: state.repoResults,
        loading: state.loading,
        searchLoading: state.searchLoading,
        repoSearchLoading: state.repoSearchLoading,
        featuredLoading: state.featuredLoading,
        featuredUsers: state.featuredUsers,
        profilesByLogin: state.profilesByLogin,
        reposByLogin: state.reposByLogin,
        insightByLogin: state.insightByLogin,
        searchMeta: state.searchMeta,
        repoSearchMeta: state.repoSearchMeta,
        insightLogin: state.insightLogin,
        rateLimit: state.rateLimit,
        apiError: state.apiError,
        searchUsers,
        searchRepositories,
        fetchFeaturedUsers,
        fetchProfileForCard,
        fetchReposForCard,
        fetchInsightBundle,
        setInsightLogin,
        clearUsers,
        clearApiError,
        resetHomeSearch,
        getUser,
        getUserRepos,
        buildUserSearchQuery,
        buildRepoSearchQuery,
      }}
    >
      {props.children}
    </GithubContext.Provider>
  );
};

export default GithubState;
